/**
 * DOM overlay that paints hover tooltips on the Pixi building
 * exterior.  Mirrors ``PixiTooltipLayer`` for the pod-room scene --
 * native ``title=`` doesn't work on a single canvas element, so we
 * recreate hover affordances via:
 *
 *   1. ``BuildingPodHitRect`` registry (populated by the scene on
 *      every rebuild, kept in a module-level singleton).
 *   2. ``pointermove`` listener on the canvas host that translates
 *      pointer coords to "user is hovering pod_X".
 *   3. An absolute-positioned card sitting above the canvas with
 *      pod state read live from the Zustand store -- numbers stay
 *      fresh as new events land while the cursor is stationary.
 *
 * The overlay is ``pointer-events: none`` so clicks/taps still flow
 * through to Pixi (pod-window click → select pod → enter room).
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { useDashboardStore } from '@/state/store';
import {
  getBuildingPodHitRects,
  hitTestBuildingPod,
  type BuildingPodHitRect,
} from '@/scenes/buildingHitRects';

const TOOLTIP_WIDTH = 280;
const TOOLTIP_OFFSET = 12;

interface HoverState {
  rect: BuildingPodHitRect;
  px: number;
  py: number;
}

const RISK_LABEL: Record<string, string> = {
  active: 'active',
  watch: 'watch',
  reduced: 'reduced',
  frozen: 'frozen',
};

const RISK_ACCENT: Record<string, string> = {
  active: '#2ea043',
  watch: '#d29922',
  reduced: '#db6d28',
  frozen: '#58a6ff',
};

const FLOOR_LABEL: Record<string, string> = {
  canary_lab: 'Canary Lab',
  active_offices: 'Active Offices',
  reduced_floor: 'Reduced Floor',
  frozen_basement: 'Frozen Basement',
};

export function BuildingTooltipLayer() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState<HoverState | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const host = container.parentElement;
    if (!host) return;

    const handleMove = (event: PointerEvent) => {
      // Touch users tap to enter the pod; a touch-and-hold tooltip
      // would block taps without adding value.
      if (event.pointerType === 'touch') return;
      const bounds = host.getBoundingClientRect();
      const px = event.clientX - bounds.left;
      const py = event.clientY - bounds.top;
      const rect = hitTestBuildingPod(px, py);
      if (rect) {
        setHover({ rect, px, py });
      } else {
        setHover((prev) => (prev ? null : prev));
      }
    };
    const handleLeave = () => setHover(null);

    host.addEventListener('pointermove', handleMove);
    host.addEventListener('pointerleave', handleLeave);
    return () => {
      host.removeEventListener('pointermove', handleMove);
      host.removeEventListener('pointerleave', handleLeave);
    };
  }, []);

  // Invalidate stale hover when the rect registry rebuilds (e.g. resize).
  useEffect(() => {
    if (!hover) return;
    const stillExists = getBuildingPodHitRects().some(
      (r) => r.podId === hover.rect.podId,
    );
    if (!stillExists) setHover(null);
  }, [hover]);

  return (
    <div
      ref={containerRef}
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 3,
      }}
    >
      {hover ? <PodHoverCard hover={hover} /> : null}
    </div>
  );
}

function PodHoverCard({ hover }: { hover: HoverState }) {
  const podId = hover.rect.podId;
  const runtime = useDashboardStore((s) => s.pods[podId] ?? null);
  const summary = useDashboardStore((s) =>
    s.building?.floors.flatMap((f) => f.pods).find((p) => p.pod_id === podId) ?? null,
  );

  // Hold a stable snapshot of risk + nav data we want to render.  All
  // come from the store / building snapshot; the tooltip stays live
  // while the user keeps the cursor stationary because Zustand
  // re-renders on relevant updates.
  const data = useMemo(() => {
    const lastNav = runtime?.navHistory?.[runtime.navHistory.length - 1] ?? null;
    const firstNav = runtime?.navHistory?.[0] ?? null;
    return {
      name: hover.rect.name,
      riskState: runtime?.riskState ?? summary?.risk_state ?? null,
      riskReason: summary?.risk_reason ?? null,
      riskSince: summary?.risk_since ?? null,
      floor: runtime?.floor ?? summary?.floor ?? null,
      isCanary: summary?.is_canary ?? false,
      capital: summary?.capital ?? null,
      nav: lastNav?.nav ?? summary?.nav ?? null,
      navOpen: firstNav?.nav ?? null,
      // Prefer broker-scale return when the backend computed it -- the
      // economic ``intradayReturn`` is amplified by sandbox topups on
      // leveraged pods and reads as +30%+ when reality is +2%.  Falls
      // through to the bootstrap snapshot's ``daily_return`` if no live
      // tick has landed yet.  See pod.nav_tick schema for the math.
      intradayReturn:
        lastNav?.brokerScaleIntradayReturn ??
        lastNav?.intradayReturn ??
        summary?.daily_return ??
        null,
      intradayPnl: lastNav?.intradayPnl ?? summary?.daily_pnl ?? null,
      navHistoryCount: runtime?.navHistory?.length ?? 0,
      latestNavTs: lastNav?.ts ?? null,
      haircutPct: runtime?.haircutPct ?? summary?.haircut_pct ?? null,
      discipline: runtime?.disciplineFlag ?? null,
      policy: runtime?.policyAction ?? null,
      centerbook: runtime?.centerbook ?? null,
      modifiers: (summary?.current_modifiers ?? {}) as Record<string, unknown>,
      utility: runtime?.utility ?? null,
    };
  }, [hover.rect.name, runtime, summary]);

  // Position: prefer right of cursor; flip left near right edge.  Top
  // is clamped to viewport top with a small inset.
  const viewportWidth =
    typeof window !== 'undefined' ? window.innerWidth - 16 : 1200;
  const flipLeft = hover.px + TOOLTIP_OFFSET + TOOLTIP_WIDTH > viewportWidth;
  const left = flipLeft
    ? Math.max(8, hover.px - TOOLTIP_OFFSET - TOOLTIP_WIDTH)
    : hover.px + TOOLTIP_OFFSET;
  const top = Math.max(8, hover.py - 8);

  const riskAccent = data.riskState ? RISK_ACCENT[data.riskState] ?? '#8b949e' : '#8b949e';

  return (
    <div
      role="tooltip"
      style={{
        position: 'absolute',
        left,
        top,
        width: TOOLTIP_WIDTH,
        background: '#0d1117f2',
        border: '1px solid #30363d',
        borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
        backdropFilter: 'blur(4px)',
        padding: '8px 10px',
        fontSize: 11,
        color: '#e6edf3',
        lineHeight: 1.45,
        zIndex: 5,
      }}
    >
      {/* Header: pod name + risk pill */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 6,
        }}
      >
        <strong style={{ fontSize: 12, letterSpacing: 0.2 }}>{data.name}</strong>
        {data.riskState ? (
          <span
            style={{
              fontSize: 9,
              textTransform: 'uppercase',
              letterSpacing: 0.7,
              padding: '1px 6px',
              borderRadius: 4,
              border: `1px solid ${riskAccent}`,
              color: riskAccent,
            }}
          >
            {RISK_LABEL[data.riskState] ?? data.riskState}
          </span>
        ) : null}
      </div>
      <div style={{ color: '#6e7681', fontSize: 9, marginBottom: 6 }}>
        {data.floor ? FLOOR_LABEL[data.floor] ?? data.floor : ''}
        {data.isCanary ? ' · canary' : ''}
      </div>

      {/* NAV / P&L block */}
      <Row k="NAV" v={fmtRub(data.nav)} />
      <Row
        k="Intraday"
        v={
          <span style={{ color: returnColor(data.intradayReturn) }}>
            {fmtSignedPct(data.intradayReturn)}
            {data.intradayPnl != null ? ` · ${fmtSignedRub(data.intradayPnl)}` : ''}
          </span>
        }
      />
      {data.capital != null ? <Row k="Capital" v={fmtRub(data.capital)} /> : null}
      {data.haircutPct != null && data.haircutPct > 0 ? (
        <Row k="Haircut" v={`-${(data.haircutPct * 100).toFixed(1)}%`} />
      ) : null}
      {data.latestNavTs ? (
        <Row k="Last tick" v={relAge(Date.now() - new Date(data.latestNavTs).getTime())} />
      ) : null}

      {/* Risk-reason block for non-active pods */}
      {data.riskState && data.riskState !== 'active' && (data.riskReason || data.riskSince) ? (
        <Section label="Risk">
          {data.riskReason ? (
            <div style={{ color: '#c9d1d9', marginBottom: 2 }}>
              {prettyRiskReason(data.riskReason)}
            </div>
          ) : null}
          {data.riskSince ? (
            <div style={{ color: '#6e7681', fontSize: 9 }}>
              {data.riskState} {relAge(Date.now() - new Date(data.riskSince).getTime())}
            </div>
          ) : null}
        </Section>
      ) : null}

      {/* Discipline flag */}
      {data.discipline ? (
        <Section label="Discipline">
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 6,
            }}
          >
            <span
              style={{
                color: severityColor(data.discipline.severity),
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: 9,
                letterSpacing: 0.5,
              }}
            >
              {data.discipline.severity}
            </span>
            <span style={{ color: '#c9d1d9' }}>{data.discipline.code}</span>
          </div>
          {Object.keys(data.discipline.detail).length > 0 ? (
            <div style={{ color: '#8b949e', fontSize: 10, marginTop: 2 }}>
              {Object.entries(data.discipline.detail)
                .slice(0, 4)
                .map(([k, v]) => `${k}: ${fmtScalar(v)}`)
                .join(' · ')}
            </div>
          ) : null}
        </Section>
      ) : null}

      {/* Policy action */}
      {data.policy ? (
        <Section label="Policy">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span
              style={{
                color: policyColor(data.policy.kind),
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: 9,
                letterSpacing: 0.5,
              }}
            >
              {data.policy.kind.replace(/_/g, ' ')}
            </span>
            <span style={{ color: '#c9d1d9' }}>{data.policy.triggerRule}</span>
          </div>
          {data.policy.paramChanged ? (
            <div style={{ color: '#8b949e', fontSize: 10, marginTop: 2 }}>
              {data.policy.paramChanged}: {fmtScalar(data.policy.valueAfter)}
            </div>
          ) : null}
        </Section>
      ) : null}

      {/* Centerbook overlay */}
      {data.centerbook && Math.abs(data.centerbook.podScale - 1) > 0.001 ? (
        <Section label="Center book">
          <div style={{ color: '#c9d1d9' }}>
            Scale {data.centerbook.podScale.toFixed(2)}×
            {data.centerbook.grossBefore != null && data.centerbook.grossAfter != null
              ? ` · gross ${fmtPct(data.centerbook.grossBefore)} → ${fmtPct(data.centerbook.grossAfter)}`
              : ''}
          </div>
        </Section>
      ) : null}

      {/* PolicyUpdater modifiers tags */}
      {Object.keys(data.modifiers).length > 0 ? (
        <Section label="Modifiers">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {Object.entries(data.modifiers)
              .slice(0, 6)
              .map(([k, v]) => (
                <span
                  key={k}
                  style={{
                    fontSize: 9,
                    padding: '1px 5px',
                    borderRadius: 4,
                    border: '1px solid #30363d',
                    color: '#8b949e',
                  }}
                >
                  {k}
                  {v != null && v !== true ? `: ${fmtScalar(v)}` : ''}
                </span>
              ))}
          </div>
        </Section>
      ) : null}

      {/* Utility */}
      {data.utility ? (
        <Section label="Utility">
          <Row k="total" v={data.utility.total.toFixed(3)} />
        </Section>
      ) : null}

      {data.navHistoryCount > 0 ? (
        <div style={{ color: '#6e7681', fontSize: 9, marginTop: 6 }}>
          {data.navHistoryCount} NAV точек в окне · клик чтобы войти в под
        </div>
      ) : (
        <div style={{ color: '#6e7681', fontSize: 9, marginTop: 6 }}>
          клик чтобы войти в под
        </div>
      )}
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 6, paddingTop: 5, borderTop: '1px solid #21262d' }}>
      <div
        style={{
          color: '#8b949e',
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 8,
        fontSize: 10,
        marginTop: 1,
      }}
    >
      <span style={{ color: '#8b949e' }}>{k}</span>
      <span style={{ color: '#e6edf3', fontVariantNumeric: 'tabular-nums' }}>{v}</span>
    </div>
  );
}

function fmtRub(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return '—';
  if (Math.abs(v) >= 1_000_000) return `₽${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `₽${(v / 1_000).toFixed(0)}k`;
  return `₽${Math.round(v)}`;
}

function fmtSignedRub(v: number): string {
  const sign = v >= 0 ? '+' : '−';
  return `${sign}${fmtRub(Math.abs(v))}`;
}

function fmtSignedPct(fraction: number | null): string {
  if (fraction == null || !Number.isFinite(fraction)) return '—';
  const pct = fraction * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
}

function fmtPct(fraction: number): string {
  return `${(fraction * 100).toFixed(0)}%`;
}

function fmtScalar(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'number') return Number.isInteger(v) ? `${v}` : v.toFixed(2);
  if (typeof v === 'boolean') return v ? 'on' : 'off';
  return String(v);
}

function returnColor(fraction: number | null): string {
  if (fraction == null) return '#8b949e';
  if (fraction > 0.0005) return '#3fb950';
  if (fraction < -0.0005) return '#f85149';
  return '#8b949e';
}

function severityColor(sev: 'low' | 'medium' | 'high' | 'critical'): string {
  switch (sev) {
    case 'critical':
      return '#ff3030';
    case 'high':
      return '#f85149';
    case 'medium':
      return '#d29922';
    case 'low':
    default:
      return '#ffd33d';
  }
}

function policyColor(kind: string): string {
  switch (kind) {
    case 'applied':
      return '#2ea043';
    case 'rolled_back':
      return '#a371f7';
    case 'shadow_recommended':
      return '#58a6ff';
    case 'no_change':
    default:
      return '#8b949e';
  }
}

/**
 * Risk reasons come in a compact backend form like
 * ``hard_breach(var=8.5,ret=-0.07)`` or ``frozen_auto_recovery``.
 * Strip the parentheses for the tooltip while preserving the rule
 * name -- the full payload still appears below if present.
 */
function prettyRiskReason(reason: string): string {
  const trimmed = reason.trim();
  if (!trimmed) return '';
  const parenIdx = trimmed.indexOf('(');
  if (parenIdx < 0) return trimmed.replace(/_/g, ' ');
  const rule = trimmed.slice(0, parenIdx).replace(/_/g, ' ');
  const args = trimmed.slice(parenIdx);
  return `${rule} ${args}`;
}

function relAge(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return 'just now';
  if (ms < 60_000) return `${Math.floor(ms / 1_000)}с назад`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}м назад`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}ч назад`;
  return `${Math.floor(ms / 86_400_000)}д назад`;
}
